// The copy-message pipeline: message parts → clean markdown → clipboard
// (HTML + plain text). Single source for the npm package, the shadcn
// registry, and (via imports) the private `@magnus/react` transcript — the
// same stripping/clipboard behavior everywhere a Magnus reply is copied.

import type { MagnusUIMessage } from "@bg-bgi/magnus-sdk";
import { marked } from "marked";

const CHICLET_REF =
  /<ref\s[^>]*label=["']([^"']*)["'][^>]*?(?:\/\s*>|>[\s\S]*?<\/ref>)/gi;

const URL_REFID = /\srefid=["'](https?:\/\/[^"'\s]+)["']/i;

/**
 * Strip `<ref …/>` chiclet tags from markdown, keeping their human-readable
 * `label`. Chiclets are a Magnus rendering concern; copied text should read
 * as prose.
 *
 * A chiclet whose `refid` is an absolute http(s) URL (a web source) becomes a
 * markdown link instead: the label alone would drop the only thing the reader
 * pasting it elsewhere can act on. Other refids (page ids, object ids, encoded
 * prompts) mean nothing outside Magnus, so those keep just the label.
 */
export function stripChicletRefs(md: string): string {
  return md.replace(CHICLET_REF, (tag, label: string) => {
    const url = URL_REFID.exec(tag)?.[1];
    if (!url) {
      return label;
    }
    const text = label.replace(/[[\]\\]/g, "\\$&");
    const href = url.replace(/[()]/g, (ch) => (ch === "(" ? "%28" : "%29"));
    return `[${text || href}](${href})`;
  });
}

/**
 * Render a single part to its canonical copied-text form, mirroring the
 * private `@workspace/ai` linearizer's rules for the part types this block
 * renders:
 *
 *   - text            → the text verbatim (trimmed; empty → skipped)
 *   - skill ref       → `{{skill:id|name}}`
 *   - artifact ref    → `{{artifact:id|name}}`
 *   - file            → `[File: filename]` when a name is known
 *
 * Unknown part types return `null` and are skipped.
 */
function linearizePart(part: MagnusUIMessage["parts"][number]): string | null {
  if (part.type === "text") {
    const text = part.text?.trim();
    return text ? text : null;
  }
  if (part.type === "data-skill-reference") {
    const { skillId, skillName } = part.data;
    return skillId && skillName ? `{{skill:${skillId}|${skillName}}}` : null;
  }
  if (part.type === "data-artifact-reference") {
    const { artifactId, artifactName } = part.data;
    return artifactId && artifactName
      ? `{{artifact:${artifactId}|${artifactName}}}`
      : null;
  }
  if (part.type === "file") {
    return part.filename ? `[File: ${part.filename}]` : null;
  }
  return null;
}

/** The markdown a message copies: its displayable parts, paragraph-joined,
 *  with chiclet `<ref>` tags reduced to their labels (or links, for URLs). */
export function markdownForCopy(message: MagnusUIMessage): string {
  const rendered: string[] = [];
  for (const part of message.parts) {
    const text = linearizePart(part);
    if (text) {
      rendered.push(text);
    }
  }
  return stripChicletRefs(rendered.join("\n\n"));
}

/**
 * Convert markdown to HTML (via `marked`, which Streamdown already
 * depends on) so pasting into a rich-text editor (Word, Google Docs,
 * email, …) preserves headings, bold, lists, code blocks, and links.
 */
export function markdownToClipboardHtml(md: string): string {
  return marked.parse(md, { async: false });
}

/**
 * Write to the system clipboard with both an HTML and a plain-text
 * representation when possible: plain-text targets get clean markdown,
 * rich-text editors silently accept the HTML. If only plain text is
 * provided (or `ClipboardItem` is unavailable), write the string directly.
 */
export async function copyToClipboard(
  plainContent: string,
  htmlContent?: string
): Promise<boolean> {
  if (
    typeof navigator !== "undefined" &&
    typeof ClipboardItem !== "undefined" &&
    navigator.clipboard?.write &&
    htmlContent !== undefined
  ) {
    try {
      const item = new ClipboardItem({
        "text/plain": new Blob([plainContent], { type: "text/plain" }),
        "text/html": new Blob([htmlContent], { type: "text/html" }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      // Fall through to writeText below.
    }
  }

  try {
    await navigator.clipboard.writeText(plainContent);
    return true;
  } catch {
    return false;
  }
}
